import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import {
  useHMSStore,
  useHMSActions,
  selectIsConnectedToRoom,
} from '@100mslive/react-sdk'
import {
  decodeInterviewToken,
  getRawJWTFromToken,
} from '../utilities/tokenUtils'
import FullLogo from '../assets/logos/full-logo.png'
import ProgressBar from '../components/progressBar'
import {
  ContentContainer,
  ImageContainer,
  InterviewQuestionTag,
  InterviewQuestionText,
  InterviewQuestionsContainerBox,
  InterviewQuestionsTextContainer,
  InterviewTimerBox,
  InterviewTimerText,
  MainContainer,
  QuestionsProgressCard,
  QuestionsProgressContainer,
  QuestionsProgressText,
  QuestionsProgressTitle,
  QuestionsProgressTitleContainer,
  Separator,
  VoiceOverButton,
} from '../components/styled'
import apiInterviewVersions from '../services/api/interviewVersion'
import RecordingSectionThirdParty from '../components/interviewVideo/RecordingSectionThirdParty'
import LoadingPage from '../components/loadingPage'
import theme from '../constants/theme'
import PrimaryButton from '../components/buttons/PrimaryButton'
import TimeCircle from '../assets/svgs/timeCircleBlue'
import Volume2Icon from '../assets/svgs/volume2'
import apiHMSVideoControl from '../services/api/hmsVideoControl'
import { voiceOver } from '../utilities/voiceOver'

function VideoInterview() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const hmsActions = useHMSActions()
  const candidateRoleRoomCode = localStorage.getItem('candidateRoleRoomCode')
  const candidateRoleRoomName = localStorage.getItem('candidateRoleRoomName')
  const isConnected = useHMSStore(selectIsConnectedToRoom)
  const { interviewToken } = useParams()
  const rawJWT = getRawJWTFromToken(interviewToken)
  const interviewId = decodeInterviewToken(interviewToken).interview_version_id
  const [question, setQuestion] = useState('')
  const [questionsNumber, setQuestionsNumber] = useState(0)
  const [questionProgress, setQuestionProgress] = useState(1)
  const [idOfCurrentQuestions, setIdOfCurrentQuestions] = useState()
  const [recordingStatus, setRecordingStatus] = useState('notAnswered')
  const [isLoading, setIsLoading] = useState(false)
  const [minutesInterview, setMinutesInterview] = useState(15)
  const [secondsInterview, setSecondsInterview] = useState(0)
  const [interviewType, setInterviewType] = useState('')
  const [recordingTime, setRecordingTime] = useState(0)

  const isCoverLetter = interviewType === 'COVERLETTER'
  const maxRecordingTime = isCoverLetter ? 180 : null
  const [enableVoiceOver, setEnableVoiceOver] = useState(false)

  const handleVoiceOver = async () => {
    try {
      await voiceOver(idOfCurrentQuestions, question)
    } catch {
      // voice over error
    }
  }

  const endRoom = async () => {
    try {
      const lock = false
      const reason = 'Interview has ended'
      await hmsActions.endRoom(lock, reason)
    } catch {
      // error captured
    }
  }

  const handleSubmitAnswer = async () => {
    setIsLoading(true)
    apiInterviewVersions
      .sendAnswer({
        interviewToken: rawJWT,
        questionId: idOfCurrentQuestions,
        video: `interview-versions/${interviewId}/Q-${idOfCurrentQuestions}.mp4`,
        audio: `interview-versions/${interviewId}/Q-${idOfCurrentQuestions}.wav`,
      })
      .then(() => {
        if (questionProgress === questionsNumber) {
          endRoom()
          localStorage.clear()
          navigate(0)
        } else
          apiInterviewVersions.getNextQuestion(rawJWT).then((res) => {
            setQuestion(res.question.toString())
            setQuestionProgress(questionProgress + 1)
            setIdOfCurrentQuestions(res.id)
            setRecordingStatus('notAnswered')
            setRecordingTime(0)
          })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    apiInterviewVersions.startInterview(rawJWT).then((data) => {
      if (data && data.remainingQuestion) {
        setQuestion(data.question.toString())
        setQuestionProgress(data.index + 1)
        setIdOfCurrentQuestions(data.id)
        setRecordingStatus('notAnswered')
        setInterviewType(data.type)
        setEnableVoiceOver(data.enableVoiceOver)
        const duration = data.duration.split('.')[0].split(':')
        setMinutesInterview(duration[1])
        setSecondsInterview(duration[2])
      } else {
        localStorage.clear()
        navigate(0)
      }
    })
    apiInterviewVersions
      .getNumberOfQuestionsByInterview(rawJWT)
      .then((res) => setQuestionsNumber(res.questions))
  }, [rawJWT, navigate])

  useEffect(() => {
    if (enableVoiceOver && question) {
      setTimeout(() => {
        handleVoiceOver()
      }, 1000)
    }
  }, [question, enableVoiceOver])

  useEffect(() => {
    const countdownInterval = setInterval(async () => {
      if (secondsInterview > 0) {
        setSecondsInterview(secondsInterview - 1)
      }
      if (secondsInterview === 0) {
        if (minutesInterview === 0) {
          clearInterval(countdownInterval)
          const roomId = localStorage.getItem('room_id')
          const managementToken = localStorage.getItem('management_token')
          await apiHMSVideoControl.stopRecording(roomId, managementToken)
          localStorage.clear()
          navigate(0)
        } else {
          setMinutesInterview(minutesInterview - 1)
          setSecondsInterview(59)
        }
      }
    }, 1000)
    return () => {
      clearInterval(countdownInterval)
    }
  }, [recordingStatus, secondsInterview, minutesInterview, navigate])

  useEffect(() => {
    let recordingInterval
    if (recordingStatus === 'recording') {
      recordingInterval = setInterval(() => {
        setRecordingTime((prevTime) => {
          const newTime = prevTime + 1
          if (isCoverLetter && newTime >= maxRecordingTime) {
            setRecordingStatus('maxTimeReached')
            return maxRecordingTime
          }
          return newTime
        })
      }, 1000)
    }
    return () => {
      if (recordingInterval) {
        clearInterval(recordingInterval)
      }
    }
  }, [recordingStatus, isCoverLetter, maxRecordingTime])

  useEffect(() => {
    const joinRoom = async () => {
      const authToken = await hmsActions.getAuthTokenByRoomCode({
        roomCode: candidateRoleRoomCode,
      })
      try {
        await hmsActions.join({ userName: candidateRoleRoomName, authToken })
      } catch {
        // join room error
      }
    }
    if (!isConnected) {
      joinRoom()
    }
  }, [candidateRoleRoomCode, candidateRoleRoomName, isConnected])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs < 10 ? `0${secs}` : secs}`
  }

  const getRemainingRecordingTime = () => {
    if (!isCoverLetter) return null
    return maxRecordingTime - recordingTime
  }

  if (!question) return <LoadingPage />

  let nextButtonBackground = theme.talentinoGray
  if (recordingStatus === 'recording' || recordingStatus === 'loading') {
    nextButtonBackground = theme.talentinoDisabledBlue
  } else if (recordingStatus === 'stopped') {
    nextButtonBackground = theme.talentinoBlue
  }

  let nextButtonHover = theme.talentinoGray
  if (recordingStatus === 'recording' || recordingStatus === 'loading') {
    nextButtonHover = theme.talentinoDisabledDarkerBlue
  } else if (recordingStatus === 'stopped') {
    nextButtonHover = theme.talentinoDarkerBlue
  }

  return (
    <MainContainer>
      <ImageContainer>
        <img height="auto" width="200px" src={FullLogo} alt="" />
      </ImageContainer>
      <ContentContainer
        justifyContent="auto"
        marginTop="50px"
        width="100vw"
        padding="0px"
      >
        <Row className="w-100">
          <Col xs={12} md={8} lg={9}>
            <RecordingSectionThirdParty
              recordingStatus={recordingStatus}
              setRecordingStatus={setRecordingStatus}
              isLoading={isLoading}
              questionId={idOfCurrentQuestions}
              recordingTime={recordingTime}
              maxRecordingTime={maxRecordingTime}
              isCoverLetter={isCoverLetter}
              onRecordingStart={() => setRecordingTime(0)}
            />
            <InterviewQuestionsContainerBox>
              <InterviewQuestionTag>
                {t('interview.question')}:{' '}
                <span
                  style={{
                    color: theme.talentinoBlue,
                  }}
                >
                  {questionProgress}
                </span>
              </InterviewQuestionTag>
              <InterviewQuestionsTextContainer>
                <div
                  className="d-flex align-items-start mb-3"
                  style={{ width: '100%' }}
                >
                  <InterviewQuestionText
                    className="flex-grow-1"
                    style={{ marginRight: enableVoiceOver ? '8px' : '0' }}
                  >
                    {question}
                    {enableVoiceOver && (
                      <VoiceOverButton
                        type="button"
                        onClick={handleVoiceOver}
                        title={t('interview.readQuestionAloud')}
                        style={{
                          position: 'relative',
                          top: '0',
                          right: '0',
                          marginLeft: '8px',
                          display: 'inline-flex',
                          verticalAlign: 'top',
                        }}
                      >
                        <Volume2Icon width="16" height="16" color="#ffffff" />
                      </VoiceOverButton>
                    )}
                  </InterviewQuestionText>
                </div>
                {questionProgress === questionsNumber ? (
                  <PrimaryButton
                    backgroundcolor={theme.talentinoGreen}
                    hovercolor={theme.talentinoDarkerGreen}
                    width="90px"
                    disabled={recordingStatus !== 'stopped'}
                    onClick={handleSubmitAnswer}
                  >
                    {t('general.submit')}
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    backgroundcolor={nextButtonBackground}
                    hovercolor={nextButtonHover}
                    width="90px"
                    disabled={recordingStatus !== 'stopped'}
                    type="button"
                    onClick={handleSubmitAnswer}
                  >
                    {t('pages.nextQuestion')}
                  </PrimaryButton>
                )}
              </InterviewQuestionsTextContainer>
            </InterviewQuestionsContainerBox>
          </Col>
          <Col xs={12} md={4} lg={3}>
            <QuestionsProgressCard>
              <QuestionsProgressTitleContainer>
                <QuestionsProgressTitle>
                  {t('interview.interviewTimeWithTalentino')}
                </QuestionsProgressTitle>
              </QuestionsProgressTitleContainer>
              <Separator />
              <InterviewTimerBox>
                <InterviewTimerText>
                  <TimeCircle width="24px" height="24px" />
                  {minutesInterview} :{' '}
                  {secondsInterview < 10
                    ? `0${secondsInterview}`
                    : secondsInterview}
                </InterviewTimerText>
              </InterviewTimerBox>
            </QuestionsProgressCard>
            {isCoverLetter && (
              <QuestionsProgressCard>
                <QuestionsProgressTitleContainer>
                  <QuestionsProgressTitle>
                    {t('interview.recordingTime')}
                  </QuestionsProgressTitle>
                </QuestionsProgressTitleContainer>
                <Separator />
                <InterviewTimerBox>
                  <InterviewTimerText>
                    <TimeCircle width="24px" height="24px" />
                    {formatTime(recordingTime)} / {formatTime(maxRecordingTime)}
                  </InterviewTimerText>
                  {getRemainingRecordingTime() <= 30 &&
                    recordingStatus === 'recording' && (
                      <div
                        style={{
                          color: theme.talentinoRed,
                          fontSize: '12px',
                          marginTop: '5px',
                        }}
                      >
                        {t('interview.recordingTimeWarning', {
                          time: getRemainingRecordingTime(),
                        })}
                      </div>
                    )}
                </InterviewTimerBox>
              </QuestionsProgressCard>
            )}
            <QuestionsProgressCard>
              <QuestionsProgressTitleContainer>
                <QuestionsProgressTitle>
                  {t('interview.questionProgress')}
                </QuestionsProgressTitle>
              </QuestionsProgressTitleContainer>
              <Separator />
              <QuestionsProgressContainer>
                <QuestionsProgressText>
                  {t('interview.questions')}{' '}
                  <span
                    style={{
                      color: theme.talentinoBlue,
                    }}
                  >
                    ({questionProgress}/{questionsNumber})
                  </span>
                </QuestionsProgressText>
                <ProgressBar
                  bgcolor={theme.talentinoBlue}
                  progress={questionProgress}
                  height={10}
                  leftText={questionsNumber}
                />
              </QuestionsProgressContainer>
            </QuestionsProgressCard>
          </Col>
        </Row>
      </ContentContainer>
    </MainContainer>
  )
}

export default VideoInterview

VideoInterview.propTypes = {
  interviewType: PropTypes.string,
}

VideoInterview.defaultProps = {
  interviewType: 'VIDEO',
}
